// @ts-nocheck

import React, { useCallback, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import WinaLoader from '../../../components/WinaLoader';
import { __ } from '../../../i18n/l10n';
import theme from '../../../styled-components/dark';
import { MPLogo } from '../assets/svg';
import MPEnrollmentButtonContainer from '../containers/MPEnrollmentButtonContainer';

import MPViewWrapper from './common/MPViewWrapper';

const APPEARANCE_ANIMATION_DURATION = 1500;

const StyledMPViewWrapper = styled(MPViewWrapper)`
  --MPViewWrapper-filter: brightness(0.6);

  ${({ $hasBeenClicked }) => css`
    ${$hasBeenClicked
      ? css`
          --MPViewWrapper-filter: brightness(1);
        `
      : undefined}
  `}
`;

const fadeInWithTranslationAnim = keyframes`
  0% {
    transform: translateY(25%);
    opacity: 0;
  }

  50% {
    transform: translateY(25%);
    opacity: 0;
  }

  100% {
    transform: translateY(0);
    opacity: 1;
  }
`;

const fadeInAnim = keyframes`
  0% {
    opacity: 0;
  }

  50% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const EnrollmentWindow = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;

  width: 80%;

  transform: translate(-50%, -50%);
  padding: 0.5em 1.2em 1.2em 1.2em;
  border-radius: 10px;

  ${({ $hasBeenClicked }) => css`
    ${$hasBeenClicked
      ? css`
          animation: ${fadeInAnim} ${APPEARANCE_ANIMATION_DURATION}ms ease-in-out forwards reverse;
        `
      : undefined}
  `}
`;

const EnrollmentWindowBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: inherit;
  background-color: ${theme.MPEnrollmentWindow.backgroundColor};
  animation: ${fadeInWithTranslationAnim} ${APPEARANCE_ANIMATION_DURATION}ms ease-in-out forwards;
  filter: drop-shadow(0px 0px 5px ${theme.MPEnrollmentWindow.backgroundColor});
`;

const EnrollmentMessage = styled.div`
  margin-bottom: 0.8em;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const EnrollmentWindowLabel = styled.div`
  font-size: 0.9em;
  color: ${theme.MPEnrollmentWindow.labelColor};
  text-align: center;
  animation: ${fadeInWithTranslationAnim} ${APPEARANCE_ANIMATION_DURATION}ms ease-in-out forwards;
`;

const StyledMPEnrollmentButtonContainer = styled(MPEnrollmentButtonContainer)`
  animation: ${fadeInWithTranslationAnim} ${APPEARANCE_ANIMATION_DURATION}ms ease-in-out forwards;
`;

const MPLogoKeyframes = keyframes`
  0% {
    opacity: 0;
    transform: scale(3.5) translateY(20%);
  }

  10% {
    opacity: 1;
  }

  50% {
    transform: scale(3.5) translateY(20%);
  }
`;

const StyledMPLogo = styled(MPLogo)`
  stroke: white;
  stroke-width: 0.4px;
  width: 4em;
  height: 4em;

  animation: ${MPLogoKeyframes} ${APPEARANCE_ANIMATION_DURATION}ms ease-in-out forwards;
`;

const fadeInLoaderAnim = keyframes`
0% {
    opacity: 0;
  }

  100% {
    opacity: 1;
  }
`;

const StyledWinaLoader = styled(WinaLoader)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  margin: 0;

  animation: ${fadeInLoaderAnim} ${APPEARANCE_ANIMATION_DURATION / 2}ms ease-in-out forwards;
`;

type MPEnrollmentProps = {
  className?: string;
};

const MPEnrollment = ({ className }: MPEnrollmentProps) => {
  const [hasBeenClicked, setHasBeenClicked] = useState(false);

  const onRegisterRequested = useCallback(() => {
    setHasBeenClicked(true);
  }, []);

  const onRegisterResponsed = useCallback(() => {
    setHasBeenClicked(false);
  }, []);

  return (
    <StyledMPViewWrapper className={className} $hasBeenClicked={hasBeenClicked}>
      <EnrollmentWindow $hasBeenClicked={hasBeenClicked}>
        <EnrollmentWindowBackground />
        <EnrollmentMessage>
          <StyledMPLogo />
          <EnrollmentWindowLabel>{__('!desc:MonopolyEnrollmentMessage')}</EnrollmentWindowLabel>
        </EnrollmentMessage>
        <StyledMPEnrollmentButtonContainer
          onRegisterRequested={onRegisterRequested}
          onRegisterResponsed={onRegisterResponsed}
          delay={APPEARANCE_ANIMATION_DURATION}
          disabled={hasBeenClicked}
        />
      </EnrollmentWindow>
      {hasBeenClicked ? <StyledWinaLoader /> : null}
    </StyledMPViewWrapper>
  );
};

export default MPEnrollment;
